import { HttpErrorResponse } from '@angular/common/http';

/** Una violación de publicación (422 de `POST /agents/:id/publish`). */
export interface ApiViolation {
  code: string;
  message: string;
}

/**
 * Mensaje de `ApiError.from` para un fallo de red sin respuesta (status 0: CORS, DNS, conexión cortada): es del
 * transporte, no de la API, así que no se enseña como detalle (`detailMessages`).
 */
export const NETWORK_ERROR_MESSAGE = 'Network error';

/**
 * Error normalizado de la API: estado HTTP, mensaje (o lista de mensajes del ValidationPipe), `x-request-id`
 * de la respuesta y las violaciones de un 422. `status === 0` es un fallo de red.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly messages: string[];
  readonly requestId: string | null;
  readonly violations: ApiViolation[];
  readonly url: string | null;
  readonly body: unknown;

  constructor(init: {
    status: number;
    message: string;
    messages?: string[];
    requestId?: string | null;
    violations?: ApiViolation[];
    url?: string | null;
    body?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.messages = init.messages ?? [init.message];
    this.requestId = init.requestId ?? null;
    this.violations = init.violations ?? [];
    this.url = init.url ?? null;
    this.body = init.body;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }

  /**
   * Los mensajes que explican el error además de su resumen (notificación, `ErrorState`): los de la API o el texto
   * de un error local. En un fallo de red se quita el genérico del transporte (`NETWORK_ERROR_MESSAGE`), que no
   * añade nada al «No se pudo contactar con la API»; el aviso de un timeout se conserva.
   */
  get detailMessages(): string[] {
    return this.isNetwork
      ? this.messages.filter(line => line !== NETWORK_ERROR_MESSAGE)
      : this.messages;
  }

  /** El scope que la API dice que falta en un 403 (`needs one of: a, b`), si lo nombra. */
  get missingScopes(): string[] {
    const match = /needs one of:\s*([^)]+)\)?/.exec(this.message);
    if (!match) return [];
    return match[1]
      .split(',')
      .map(scope => scope.trim())
      .filter(scope => scope.length > 0);
  }

  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error;
    if (error instanceof HttpErrorResponse) {
      const body = error.error as
        | { message?: string | string[]; violations?: ApiViolation[]; statusCode?: number }
        | string
        | null
        | undefined;
      const rawMessage = typeof body === 'object' && body !== null ? body.message : undefined;
      const messages = Array.isArray(rawMessage)
        ? rawMessage.map(String)
        : rawMessage
          ? [String(rawMessage)]
          : [];
      const message =
        messages.length > 0
          ? messages.join(' · ')
          : error.status === 0
            ? NETWORK_ERROR_MESSAGE
            : error.message || `HTTP ${error.status}`;
      return new ApiError({
        status: error.status,
        message,
        messages: messages.length > 0 ? messages : [message],
        requestId: error.headers?.get('x-request-id') ?? null,
        violations:
          typeof body === 'object' && body !== null && Array.isArray(body.violations)
            ? body.violations
            : [],
        url: error.url,
        body,
      });
    }
    if (error instanceof Error) return new ApiError({ status: 0, message: error.message });
    return new ApiError({ status: 0, message: String(error) });
  }
}
